/*
 * Dashboard "Operaciones sin Facturar", réplica del HTML del Comité de Gerencia.
 * Conserva del proyecto: tipografía, Select/Checkbox/Input del codekit y márgenes
 * horizontales centrados. El resto replica el HTML original.
 */
import { useMemo, useState } from "react";
import { Box, Checkbox, Description, Select, Title } from "codekit";

import Funnelmd from "./Funnelmd";
import Tabclien from "./Tabclien";
import Tabdetal from "./Tabdetal";
import Tabimpor from "./Tabimpor";
import Tabresum from "./Tabresum";
import { carteraDataset } from "./utils/dataset";
import { carteraMeta, fmtCOP, htmlTheme, prepareRows } from "./utils/carthelp";

const TABS = [
  { id: "resumen", label: "📊 Resumen" },
  { id: "importaciones", label: "🚦 Importaciones — Semáforo" },
  { id: "clientes", label: "👥 Clientes y Directores" },
  { id: "detalle", label: "📋 Detalle" },
];

/**
 * Construye las opciones de un filtro global con su placeholder
 * @param {Array} mData Datos base
 * @param {string} sKey Clave a listar
 * @param {string} sAll Texto de la opción "todos"
 * @returns {Array} Opciones [{ value, text }]
 */
const buildOptions = (mData, sKey, sAll) => {
  const mVals = [...new Set(mData.map((r) => r[sKey]))].filter(Boolean).sort();
  return [{ value: "", text: sAll }, ...mVals.map((v) => ({ value: v, text: v }))];
};

//Estilo base de las barras de opciones (réplica del .options-bar del HTML)
const optionsBarStyle = {
  display: "flex",
  gap: 18,
  flexWrap: "wrap",
  alignItems: "center",
  background: htmlTheme.card,
  border: `1px solid ${htmlTheme.border}`,
  borderRadius: 12,
  padding: "11px 16px",
  marginBottom: 14,
};

const Dashgraf = () => {
  const [sTab, setTab] = useState("resumen");
  const [sHoverTab, setHoverTab] = useState(null);
  const [bExcludeGlobal, setExcludeGlobal] = useState(true);
  const [bIncludeNegatives, setIncludeNegatives] = useState(false);
  const [sGerencia, setGerencia] = useState("");
  const [sTipo, setTipo] = useState("");
  const [mFunnel, setFunnel] = useState(null);

  //Normaliza el dataset una sola vez
  const mRaw = useMemo(() => prepareRows(carteraDataset), []);

  //Base con la exclusión de GLOBAL aplicada
  const mBase = useMemo(
    () => (bExcludeGlobal ? mRaw.filter((r) => r["TIPO DE OPERACIÓN"] !== "GLOBAL") : mRaw),
    [mRaw, bExcludeGlobal],
  );

  //Aplica el toggle de valores negativos
  const mToggled = useMemo(
    () => (bIncludeNegatives ? mBase : mBase.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL" || r.VALOR >= 0)),
    [mBase, bIncludeNegatives],
  );

  //Aplica los filtros globales de gerencia y tipo
  const mData = useMemo(() => {
    let mList = mToggled;
    if (sGerencia) mList = mList.filter((r) => r.GERENCIA === sGerencia);
    if (sTipo) mList = mList.filter((r) => r["TIPO DE OPERACIÓN"] === sTipo);
    return mList;
  }, [mToggled, sGerencia, sTipo]);

  //Avisos de registros GLOBAL y negativos
  const mGlobalRows = useMemo(() => mRaw.filter((r) => r["TIPO DE OPERACIÓN"] === "GLOBAL"), [mRaw]);
  const nGlobalSum = mGlobalRows.reduce((a, r) => a + r.VALOR, 0);
  const mNegRows = useMemo(() => mBase.filter((r) => r.VALOR < 0), [mBase]);
  const nNegSum = mNegRows.reduce((a, r) => a + r.VALOR, 0);

  //Abre el modal de drill-down con el nivel inicial
  const openFunnel = (mScope, mKeys, mBreadcrumb) =>
    setFunnel({ scope: mScope, keys: mKeys, breadcrumb: mBreadcrumb });

  //Limpia los filtros globales
  const clearFilters = () => {
    setGerencia("");
    setTipo("");
  };

  return (
    <Box spacing={4}>
      <div style={{ maxWidth: 1260, margin: "0 auto" }}>
        <div
          style={{
            background: `linear-gradient(120deg, ${htmlTheme.navy} 0%, ${htmlTheme.navy2} 55%, ${htmlTheme.navyLight} 100%)`,
            color: "#fff",
            padding: "20px 32px",
            borderRadius: "12px 12px 0 0",
            boxShadow: "0 3px 14px rgba(18,36,89,.25)",
          }}
        >
          <Title variant="title-1">Operaciones sin Facturar</Title>
          <div style={{ opacity: 0.85, marginTop: 3 }}>
            <Description variant="description-3">
              Publicado: {carteraMeta.publishedAt} · hoja "{carteraMeta.sheetName}" ·{" "}
              {mData.length.toLocaleString("es-CO")} DO
            </Description>
          </div>
        </div>

        <div style={{ display: "flex", gap: 4, background: htmlTheme.navy, padding: "0 28px", overflowX: "auto", borderRadius: "0 0 12px 12px", marginBottom: 22 }}>
          {TABS.map((mT) => (
            <button
              key={mT.id}
              onClick={() => setTab(mT.id)}
              onMouseEnter={() => setHoverTab(mT.id)}
              onMouseLeave={() => setHoverTab(null)}
              style={{
                background: "transparent",
                border: "none",
                color: sTab === mT.id || sHoverTab === mT.id ? "#fff" : "rgba(255,255,255,.65)",
                padding: "13px 18px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                borderBottom: `3px solid ${sTab === mT.id ? htmlTheme.coral : "transparent"}`,
                whiteSpace: "nowrap",
                transition: "color .15s ease",
              }}
            >
              {mT.label}
            </button>
          ))}
        </div>

        <div style={optionsBarStyle}>
          <Checkbox checked={bExcludeGlobal} onChange={(e) => setExcludeGlobal(e.target.checked)}>
            Excluir ajustes "GLOBAL"
          </Checkbox>
          <Checkbox checked={bIncludeNegatives} onChange={(e) => setIncludeNegatives(e.target.checked)}>
            Incluir valores negativos (notas crédito) en los totales
          </Checkbox>
          {mNegRows.length ? (
            <span style={{ color: htmlTheme.muted }}>
              <Description variant="description-3">
                {mNegRows.length} registros negativos ({fmtCOP(nNegSum)}) — hoy {bIncludeNegatives ? "incluidos" : "excluidos"} del total
              </Description>
            </span>
          ) : null}
        </div>

        <div style={{ ...optionsBarStyle, gap: 10 }}>
          <span style={{ fontWeight: 700, color: htmlTheme.navy2 }}>
            <Description variant="description-3">Filtrar todo el dashboard por:</Description>
          </span>
          <div style={{ minWidth: 200 }}>
            <Select options={buildOptions(mToggled, "GERENCIA", "Todas las gerencias")} value={sGerencia} onChangeValue={({ option }) => setGerencia(option.value)} />
          </div>
          <div style={{ minWidth: 230 }}>
            <Select options={buildOptions(mToggled, "TIPO DE OPERACIÓN", "Todos los tipos de operación")} value={sTipo} onChangeValue={({ option }) => setTipo(option.value)} />
          </div>
          <button
            onClick={clearFilters}
            style={{ background: "#F0F2F9", border: "none", borderRadius: 7, padding: "7px 12px", fontSize: 12.5, cursor: "pointer", color: htmlTheme.navy2, fontWeight: 600 }}
          >
            ✕ Limpiar filtros
          </button>
          <span style={{ color: htmlTheme.muted }}>
            <Description variant="description-3">El año se filtra con las tarjetas de abajo (Resumen), no aquí</Description>
          </span>
        </div>

        {mGlobalRows.length ? (
          <div style={{ background: "#FFF4E5", color: "#B45309", border: "1px solid #FBD9A5", borderRadius: 10, padding: "11px 15px", marginBottom: 14 }}>
            <Description variant="description-3">
              ⚠️ Los registros tipo GLOBAL ({mGlobalRows.length}) suman {fmtCOP(nGlobalSum)} y pueden distorsionar el neto — revísalos con el equipo antes de reportar.
            </Description>
          </div>
        ) : null}

        {sTab === "resumen" && <Tabresum data={mData} onFunnel={openFunnel} />}
        {sTab === "importaciones" && <Tabimpor data={mData} onFunnel={openFunnel} />}
        {sTab === "clientes" && <Tabclien data={mData} onFunnel={openFunnel} />}
        {sTab === "detalle" && <Tabdetal data={mData} />}

        {mFunnel ? <Funnelmd initial={mFunnel} onClose={() => setFunnel(null)} /> : null}
      </div>
    </Box>
  );
};

export default Dashgraf;
